import { FocusTabCallbacks } from '@/components/PageHeader/PageHeader.model'

/**
 * Checks if the `window` object is defined.
 *
 * This utility function is used to determine whether the code is running
 * in an environment where the `window` object is available, such as a browser.
 *
 * @returns {boolean} `true` if the `window` object is defined, otherwise `false`.
 */
const isWindowDefined = () => typeof window !== 'undefined'

/**
 * Retrieves the first element within the document that matches the specified CSS selector.
 *
 * @param selector - A string containing a CSS selector to match the desired element.
 * @returns The first Element within the document that matches the specified selector, 
 *          or `null` if no matches are found.
 */
const getElement = (selector: string) => document.querySelector(selector)

/**
 * Removes a specified CSS class from the given HTML element if the element exists.
 *
 * @param element - The HTML element from which the class should be removed. Can be `null`.
 * @param className - The name of the CSS class to remove.
 */
const removeActiveClass = (element: HTMLElement | null, className: string) => {
  if (element) {
    element.classList.remove(className)
  }
}

/**
 * Adds a specified CSS class to the given HTML element if the element is not null.
 *
 * @param element - The HTML element to which the class will be added. Can be null.
 * @param className - The name of the CSS class to add to the element.
 */
const addActiveClass = (element: HTMLElement | null, className: string) => {
  if (element) {
    element.classList.add(className)
  }
}

/**
 * Clones a given sibling list element and appends it to a specified wrapper element.
 * The wrapper's inner HTML is cleared before appending the cloned sibling list.
 *
 * @param siblingList - The HTML element representing the sibling list to be cloned.
 * @param wrapper - The HTML element where the cloned sibling list will be appended.
 */
const cloneAndAppendSiblingList = (
  siblingList: HTMLElement,
  wrapper: HTMLElement
) => {
  const cloneSiblingList = siblingList.cloneNode(true)
  wrapper.innerHTML = ''
  wrapper.append(cloneSiblingList)
}

/**
 * Updates the level two menu by cloning and appending the sibling menu list,
 * adjusting styles, and setting the active menu text.
 *
 * @param levelTwoMenuListWrapper - The wrapper element for the level two menu list.
 * @param levelThreeMenuListWrapper - The wrapper element for the level three menu list.
 * @param clickedMenuItem - The menu item that was clicked.
 *
 * This function performs the following actions:
 * - Clones and appends the sibling menu list of the clicked menu item to the level three menu list wrapper.
 * - Resets the `transform` and `display` styles of the level two menu list wrapper.
 * - Updates the text of the active level two menu element to match the clicked menu item's text.
 */
const updateLevelTwoMenu = (
  levelTwoMenuListWrapper: HTMLElement,
  levelThreeMenuListWrapper: HTMLElement,
  clickedMenuItem: HTMLElement
) => {
  const siblingMenuList = clickedMenuItem.nextElementSibling as HTMLElement;
  cloneAndAppendSiblingList(siblingMenuList, levelThreeMenuListWrapper);
  levelTwoMenuListWrapper.style.transform = 'none';
  levelTwoMenuListWrapper.style.display = 'block';
  const activeLevelTwoMenuText = getElement('#activeLevelTwoMenu') as HTMLElement;
  activeLevelTwoMenuText.innerHTML = clickedMenuItem.innerText;
};

/**
 * Removes all list items (`<li>`) that contain elements with the class 
 * `emptyProductsForProductSuite` from the DOM. This function searches for 
 * all elements with the specified class, finds their closest parent `<li>` 
 * element, and removes it from the DOM if it exists.
 *
 * @remarks
 * This utility is intended to clean up empty product suite links in the 
 * navigation bar or similar UI components.
 *
 * @example
 * // Assuming the DOM contains:
 * // <ul>
 * //   <li><div class="emptyProductsForProductSuite"></div></li>
 * //   <li><div>Valid Product</div></li>
 * // </ul>
 * //
 * // After calling removeEmptyProductSuiteLinks():
 * // <ul>
 * //   <li><div>Valid Product</div></li>
 * // </ul>
 */
const removeEmptyProductSuiteLinks = () => {
  const productSuiteList = document.querySelectorAll<HTMLElement>(
    '.emptyProductsForProductSuite'
  )
  productSuiteList.forEach((element) => {
    const closestLi = element.closest('li')
    if (closestLi) {
      closestLi.remove()
    }
  })
}

/**
 * Toggles the visibility and updates the state of a level 2 menu in a mobile navigation bar.
 *
 * @param event - The event object triggered by the user's interaction.
 * @param levelTwoMenuWrapperRef - A React ref object pointing to the level 2 menu wrapper element.
 * @param triggerHamburgerCloseIconTimer - A callback function to trigger the hamburger close icon timer.
 *
 * The function performs the following actions:
 * - Prevents default behavior for anchor (`<a>`) elements with a non-empty `nextElementSibling`.
 * - Adds an active class to the clicked element.
 * - Updates the level 2 menu and its associated level 3 menu based on the clicked element.
 * - Removes empty product suite links from the menu.
 * - Invokes the `triggerHamburgerCloseIconTimer` callback.
 *
 * If the `window` object is not defined, the function exits early.
 */
export const toggleLevel2Callback = (
  event: any,
  levelTwoMenuWrapperRef: React.RefObject<any>,
  triggerHamburgerCloseIconTimer: () => void
) => {
  if (!isWindowDefined()) return

  const ACTIVE_CLASS_NAME = 'is-active'
  const levelTwoMenuWrapper = levelTwoMenuWrapperRef.current as any
  const levelThreeMenuWrapper =
    levelTwoMenuWrapper?.querySelector('.subMenuWrapper')
  const clickedElement = event.target

  if (
    clickedElement.tagName.toLowerCase() === 'a' &&
    clickedElement?.nextElementSibling?.innerHTML
  ) {
    event.preventDefault()
    addActiveClass(clickedElement, ACTIVE_CLASS_NAME)
    updateLevelTwoMenu(
      levelTwoMenuWrapper,
      levelThreeMenuWrapper,
      clickedElement
    )
    removeEmptyProductSuiteLinks()
    triggerHamburgerCloseIconTimer()
  }
}

/**
 * Handles the "Escape" key press event to toggle the visibility of a menu.
 *
 * @param event - The keyboard event triggered by the user.
 * @param menuWrapperRef - A React ref object pointing to the menu wrapper element.
 * @param toggleMenuCallback - A callback function to toggle the menu's visibility.
 *
 * The function checks if the pressed key is "Escape" and if the menu wrapper element
 * has the class `isVisible`. If both conditions are met, it invokes the `toggleMenuCallback`
 * to hide the menu.
 */
export const escapeKeyPressed = (
  event: any,
  menuWrapperRef: React.RefObject<any>,
  toggleMenuCallback: () => void
) => {
  if (event.key === 'Escape') {
    const menuWrapper = menuWrapperRef.current as any
    if (menuWrapper?.classList.contains('isVisible')) {
      toggleMenuCallback()
    }
  }
}

/**
 * Handles the "Enter" key press event and triggers the appropriate navigation callback
 * based on the `id` of the target element.
 *
 * @param event - The keyboard event triggered by the user.
 * @param navigateToLevelOne - Callback function to navigate to level one.
 * @param navigateToLevelTwo - Callback function to navigate to level two.
 * @param navigateToLevelThree - Callback function to navigate to level three.
 *
 * The function checks if the pressed key is "Enter" and determines the appropriate
 * navigation callback to invoke based on the `id` of the target element. The `id` of
 * the target element should match one of the keys in the `navigationCallbacks` object
 * (`levelOne`, `levelTwo`, or `levelThree`).
 */
export const enterKeyPressed = (
  event: KeyboardEvent,
  navigateToLevelOne: () => void,
  navigateToLevelTwo: () => void,
  navigateToLevelThree: () => void
) => {
  if (event.key === 'Enter') {
    const navigationCallbacks = {
      levelOne: navigateToLevelOne,
      levelTwo: navigateToLevelTwo,
      levelThree: navigateToLevelThree,
    }
    const targetElement = event.target as HTMLElement
    const navigationCallback =
      navigationCallbacks[targetElement.id as keyof typeof navigationCallbacks]
    if (navigationCallback) navigationCallback()
  }
}

/**
 * Handles the "Enter" key press event.
 *
 * This utility function is used to handle actions triggered by pressing the "Enter" key.
 *
 * @param enterKeyPressed - A callback function or handler that is executed when the "Enter" key is pressed.
 */
export const handleEnterKeyPress = enterKeyPressed

/**
 * Sets focus on the first tab and attaches event listeners for keyboard navigation.
 *
 * @param focusableElement - The first focusable HTML element to set focus on. Can be null.
 * @param lastFocusableElement - The last focusable HTML element to attach a focusout event listener. Can be null.
 * @param focusCallbacks - An object containing callback functions for handling specific focus-related events:
 *   - `enterKeyPressed`: Callback triggered when the Enter key is pressed on the focusable element.
 *   - `lastItemFocusOut`: Callback triggered when the last focusable element loses focus.
 */
export const setFocusOnFirstTab = (
  focusableElement: HTMLElement | null,
  lastFocusableElement: HTMLElement | null,
  focusCallbacks: FocusTabCallbacks
) => {
  focusableElement?.setAttribute('tabindex', '0')
  focusableElement?.focus()
  focusableElement?.addEventListener(
    'keydown',
    focusCallbacks.enterKeyPressed,
    false
  )
  lastFocusableElement?.addEventListener(
    'focusout',
    focusCallbacks.lastItemFocusOut,
    false
  )
}

/**
 * Focuses on a specific navigation level and sets up event handlers for keyboard and focus interactions.
 *
 * @param levelId - The ID of the navigation level element to focus on.
 * @param callbacks - An object containing callback functions for handling various interactions.
 * @param callbacks.setFocusOnFirstTab - A function to set focus on the first tab within the navigation level.
 * @param callbacks.handleEnterKeyPress - A function to handle the "Enter" key press event, allowing navigation between levels.
 * @param callbacks.navigateToPreviousLevel - A function to navigate to the previous navigation level.
 * @param callbacks.navigateToSecondLevel - A function to navigate to the second navigation level.
 * @param callbacks.navigateToThirdLevel - A function to navigate to the third navigation level.
 * @param callbacks.handleLastItemFocusOut - A function to handle the focus out event on the last item in the navigation level.
 */
const focusOnLevelGeneric = (
  levelId: string,
  callbacks: {
    setFocusOnFirstTab: (
      levelElement: HTMLElement | null,
      lastItemElement: HTMLElement | null,
      focusTabCallbacks: FocusTabCallbacks
    ) => void
    handleEnterKeyPress: (
      event: KeyboardEvent,
      navigateToPreviousLevel: () => void,
      navigateToSecondLevel: () => void,
      navigateToThirdLevel: () => void
    ) => void
    navigateToPreviousLevel: () => void
    navigateToSecondLevel: () => void
    navigateToThirdLevel: () => void
    handleLastItemFocusOut: (event: FocusEvent) => void
  }
) => {
  const {
    setFocusOnFirstTab,
    handleEnterKeyPress,
    navigateToPreviousLevel,
    navigateToSecondLevel,
    navigateToThirdLevel,
    handleLastItemFocusOut,
  } = callbacks

  const levelElement = document.getElementById(levelId) as HTMLElement
  const lastItemElement = levelElement?.nextElementSibling?.nextElementSibling
    ?.firstChild?.lastChild as HTMLElement
  setFocusOnFirstTab(levelElement, lastItemElement, {
    enterKeyPressed: (event: KeyboardEvent) =>
      handleEnterKeyPress(
        event,
        navigateToPreviousLevel,
        navigateToSecondLevel,
        navigateToThirdLevel
      ),
    lastItemFocusOut: handleLastItemFocusOut,
  })
}

/**
 * Focuses on a specific navigation level and sets up the necessary callbacks for handling
 * keyboard navigation and focus management within the navigation structure.
 *
 * @param levelId - The ID of the navigation level to focus on.
 * @param callbacks - An object containing various callback functions for managing focus and navigation.
 * @param callbacks.setFocusOnFirstTab - A function to set focus on the first tab of the specified level.
 * @param callbacks.handleEnterKeyPress - A function to handle the "Enter" key press event, allowing navigation
 * between levels.
 * @param callbacks.navigateToPreviousLevel - A function to navigate to the previous navigation level.
 * @param callbacks.navigateToSecondLevel - A function to navigate to the second navigation level.
 * @param callbacks.navigateToThirdLevel - A function to navigate to the third navigation level.
 * @param callbacks.handleLastItemFocusOut - A function to handle the focus out event on the last item of the level.
 */
export const focusOnLevel = (
  levelId: string,
  callbacks: {
    setFocusOnFirstTab: (
      level: HTMLElement | null,
      levelLastItem: HTMLElement | null,
      callbacks: FocusTabCallbacks
    ) => void
    handleEnterKeyPress: (
      event: KeyboardEvent,
      navigateToPreviousLevel: () => void,
      navigateToSecondLevel: () => void,
      navigateToThirdLevel: () => void
    ) => void
    navigateToPreviousLevel: () => void
    navigateToSecondLevel: () => void
    navigateToThirdLevel: () => void
    handleLastItemFocusOut: (event: FocusEvent) => void
  }
) => {
  focusOnLevelGeneric(levelId, callbacks)
}

/**
 * Focuses on the "levelThree" navigation element by invoking the `focusOnLevel` function.
 *
 * @param callbacks - The callback functions to be passed to the `focusOnLevel` function.
 * These callbacks are used to handle specific actions or events during the focus operation.
 */
export const focusOnLevelThree = (
  callbacks: Parameters<typeof focusOnLevel>[1]
) => {
  focusOnLevel('levelThree', callbacks)
}

/**
 * Focuses on the second level of navigation by invoking the `focusOnLevel` function
 * with the specified level and callbacks.
 *
 * @param callbacks - The callbacks to be passed to the `focusOnLevel` function.
 * These callbacks are used to handle focus-related logic for the second level.
 */
export const focusOnLevelTwo = (
  callbacks: Parameters<typeof focusOnLevel>[1]
) => {
  focusOnLevel('levelTwo', callbacks)
}

/**
 * Toggles back one level in a navigation menu by resetting styles and removing the active class
 * from the previously active element. It also sets a timer to focus on the hamburger close icon.
 *
 * @param levelOneMenuListWrapperRef - A React ref object pointing to the wrapper element of the level one menu list.
 * @param setTimerOnHamburgerCloseIconFocus - A callback function to set a timer for focusing on the hamburger close icon.
 */
export const togglebackOneLevelCallback = (
  levelOneMenuListWrapperRef: React.RefObject<any>,
  setTimerOnHamburgerCloseIconFocus: () => void
) => {
  const isActiveClass = 'is-active'
  const parent = levelOneMenuListWrapperRef.current as any
  
  if (!isWindowDefined()) {
    return
  } 

  if (parent) {
    parent.style = ''
    const activeElement =
      parent.previousElementSibling?.querySelector('.is-active')
    removeActiveClass(activeElement, isActiveClass)
    setTimerOnHamburgerCloseIconFocus()
  }
}

/**
 * Focuses on the appropriate element based on the site brand and sets up a focusout event listener
 * for the last item in the navigation level.
 *
 * @param levelZero - The root HTML element of the navigation level.
 * @param levelZeroLastItem - The last HTML element in the navigation level.
 * @param lastItemFocusOut - A callback function to handle the focusout event for the last item.
 *
 * The behavior differs based on the `SITE_BRAND` environment variable:
 * - If `SITE_BRAND` is 'elavon', it focuses on the mobile search input element with the ID `#edp_search_input_mobile`.
 * - Otherwise, it focuses on the first child element of the `levelZero` element.
 *
 * Additionally, it attaches a `focusout` event listener to the `levelZeroLastItem` element.
 */
export const focusOnLevelZero = (
  levelZero: HTMLElement,
  levelZeroLastItem: HTMLElement,
  lastItemFocusOut: (event: any) => void
) => {
  if (process.env.SITE_BRAND === 'elavon') {
    const inputSearch = getElement('#edp_search_input_mobile') as HTMLElement
    inputSearch?.focus()
  } else {
    const levelZeroFirstItem = levelZero?.firstChild?.firstChild as HTMLElement
    levelZeroFirstItem?.focus()
  }
  
  levelZeroLastItem.addEventListener('focusout', lastItemFocusOut, false)
}
